import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  label: string;
  options: SelectOption[];
  hint?: ReactNode;
  error?: ReactNode;
};

/**
 * Native `<select>` styled to match the floating-label `Field` family.
 * Keeps native UX (mobile pickers, keyboard) without re-implementing a popup.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, hint, error, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `select-${reactId}`;
  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'ui-field relative',
          error && '!border-[color:var(--color-rose)]',
        )}
      >
        <select
          ref={ref}
          id={fieldId}
          {...rest}
          className={cn(
            'field-input focus-ring appearance-none pr-10 bg-transparent w-full outline-none',
          )}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <label htmlFor={fieldId} className="field-label">
          {label}
        </label>
        <ChevronDown
          size={14}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-400"
        />
      </div>
      {(hint || error) && (
        <p
          className={cn(
            'mt-1.5 text-[11.5px] leading-snug',
            error ? 'text-[color:var(--color-rose)]' : 'text-ink-500',
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
